import { doc, setDoc } from 'firebase/firestore';
import type { LegalEntity, LegalForm } from '../types';
import { newId } from '../utils/id';
import { db } from './firebase';
import { COLLECTION_LEGAL_ENTITIES, ownedItemDocId } from './firestorePaths';

export type LegalEntityInput = {
  id?: string;
  name: string;
  legalForm: LegalForm;
  seat?: string;
  parentEntityId?: string | null;
  /** Ownership share held by the parent, in basis points (10000 = 100 %). */
  ownershipBps?: number | null;
  /** When updating, pass fields from app state — Firestore denies get on non-existent owned docs. */
  existing?: Pick<LegalEntity, 'createdAt' | 'archivedAt'>;
};

export async function saveLegalEntity(
  userId: string,
  input: LegalEntityInput
): Promise<LegalEntity> {
  const name = input.name.trim();
  if (!name) {
    throw new Error('Entity name is required.');
  }

  const parentEntityId = input.parentEntityId || null;
  const ownershipBps = parentEntityId ? input.ownershipBps ?? null : null;
  if (parentEntityId && (ownershipBps === null || ownershipBps < 0 || ownershipBps > 10000)) {
    throw new Error('Enter an ownership percentage between 0 and 100.');
  }

  const now = new Date().toISOString();
  const id = input.id?.trim() || newId();
  const docRef = doc(db, COLLECTION_LEGAL_ENTITIES, ownedItemDocId(userId, id));

  const entity: LegalEntity = {
    id,
    userId,
    name,
    legalForm: input.legalForm,
    seat: input.seat?.trim() || '',
    parentEntityId,
    ownershipBps,
    archivedAt: input.existing?.archivedAt ?? null,
    createdAt: input.existing?.createdAt ?? now,
    updatedAt: now,
  };

  await setDoc(docRef, entity, { merge: true });

  return entity;
}

/** Archives instead of deleting — the entity stays in Firestore but is hidden from views. */
export async function archiveLegalEntity(userId: string, id: string): Promise<void> {
  const now = new Date().toISOString();
  const docRef = doc(db, COLLECTION_LEGAL_ENTITIES, ownedItemDocId(userId, id));
  await setDoc(docRef, { archivedAt: now, updatedAt: now }, { merge: true });
}
